<template>
    <div class="card bsa-page">
        <PageHeader
            title="Statement of Financial Position · Analytics"
            :subtitle="subtitle"
            :showBtn="false"
            :showFilterBtn="true"
            @onClickFilterButton="onClickFilterButton"
        />
        <div class="bsa-page-body">
            <BalanceSheetAnalyticsToolbar
                v-model:columnBy="columnBy"
                v-model:period="period"
                :periods="periods"
                :businesses="businesses"
                :projects="projects"
                :businessId="businessId"
                :projectId="projectId"
                @go="getReport"
                @apply="onApplyFilters"
                @remove-chip="onRemoveChip"
                @clear="onClearFilters"
                @export="exportReport"
            />
            <BalanceSheetAnalyticsSummaryCards
                :assetTotal="sectionTotal(asset)"
                :liabilityTotal="sectionTotal(liability)"
                :equityTotal="sectionTotal(equity)"
                :decimals="decimals"
                :asOf="reportEndLabel"
                :loading="loading"
            />
            <BalanceSheetAnalyticsTable
                :asset="asset"
                :equity="equity"
                :liability="liability"
                :columns="columns"
                :noOfDecimals="decimals"
                :showAccountCode="!!showAccountCode"
                :totalLabel="totalLabel"
                :loading="loading"
            />
        </div>
        <FilterModal v-if="isModalOpen" @on-filter="onFilter"/>
    </div>
</template>

<script setup>
import { ref, computed, inject, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import handleAccounting from '@/services/modules/accounting'
import handleCBusinesses from '@/services/modules/businesses'
import handleProjects from '@/services/modules/procurement/project'
import handleInventory from '@/services/modules/inventory'
import { formatToDdMmYy } from '@/services/utils/global'
import PageHeader from '@/components/atom/PageHeader.vue'
import FilterModal from '@/components/atom/FilterModal.vue'
import BalanceSheetAnalyticsToolbar from '@/components/molecule/company/balance-sheet/BalanceSheetAnalyticsToolbar.vue'
import BalanceSheetAnalyticsSummaryCards from '@/components/molecule/company/balance-sheet/BalanceSheetAnalyticsSummaryCards.vue'
import BalanceSheetAnalyticsTable from '@/components/molecule/company/balance-sheet/BalanceSheetAnalyticsTable.vue'

const $store = useStore()
const $route = useRoute()
const $router = useRouter()
const showError = inject('showError')

const { getBalanceSheetAnalytics } = handleAccounting()
const { fetchBusinessList } = handleCBusinesses()
const { fetchProjects } = handleProjects()
const { fetchHome } = handleInventory()

const columnByLabels = {
    business: 'Business Unit',
    project: 'Project'
}

const routeNumber = (value, fallback) => {
    const parsed = parseInt(value)
    return isNaN(parsed) ? fallback : parsed
}

const loading = ref(false)
const columnBy = ref('period')
const period = ref('monthly')
const businessId = ref(null)
const projectId = ref(null)
const businesses = ref([])
const projects = ref([])
const periods = ref([])
const reportData = ref({})
const reportEndDate = ref('')
const loadedQuery = ref('')
const reportColumnBy = ref('')
const reportPeriod = ref('')
const decimals = ref(routeNumber($route.query.decimals, 2))
const showNonZero = ref(routeNumber($route.query.show_non_zero, 1))
const showAccountCode = ref(routeNumber($route.query.show_account_code, 0))

const companyId = computed(() => $route.params.companyId)
const isModalOpen = computed(() => $store.state.isModalOpen)
const asset = computed(() => reportData.value.asset ? reportData.value.asset : [])
const equity = computed(() => reportData.value.equity ? reportData.value.equity : [])
const liability = computed(() => reportData.value.liability ? reportData.value.liability : [])
const columns = computed(() => reportData.value.columns ? reportData.value.columns : [])
const isDimensionReport = computed(() => ['business', 'project'].includes(reportColumnBy.value))
const reportEndLabel = computed(() => reportEndDate.value ? formatToDdMmYy(reportEndDate.value) : '')
const totalLabel = computed(() => isDimensionReport.value ? 'Total' : `As of ${reportEndLabel.value}`)

const reportColumnLabel = computed(() => {
    if (reportColumnBy.value !== 'period') return columnByLabels[reportColumnBy.value]
    const option = periods.value.find(item => item.key === reportPeriod.value)
    return option ? option.name : reportPeriod.value
})

const subtitle = computed(() => {
    if (!reportEndLabel.value) return 'Choose a date range and press Go'
    return `As of ${reportEndLabel.value} · ${reportColumnLabel.value} columns`
})

const sectionTotal = (nodes) => nodes.reduce((sum, node) => sum + (node.total || 0), 0)

const getQuery = () => {
    const params = {
        company_id: companyId.value,
        start_date: $route.query.start,
        end_date: $route.query.end,
        column_by: columnBy.value,
        period: columnBy.value === 'period' ? period.value : null,
        business_id: businessId.value,
        project_id: projectId.value,
        decimals: decimals.value,
        show_non_zero: showNonZero.value,
        show_account_code: showAccountCode.value
    }
    const filledParams = Object.keys(params)
        .filter(key => params[key] !== null && params[key] !== undefined && params[key] !== '')
        .reduce((filled, key) => ({ ...filled, [key]: params[key] }), {})
    return `?${new URLSearchParams(filledParams).toString()}`
}

const getErrorMessage = (err) => {
    if (!err || !err.response || !err.response.data) return 'Something is wrong. Check your connectivity!!'
    return err.response.data.message
}

const getReport = async () => {
    if (!$route.query.start || !$route.query.end) return
    loading.value = true
    const query = getQuery()
    try {
        const res = await getBalanceSheetAnalytics(query)
        if (!res.status) {
            showError(res.message)
            return
        }
        loadedQuery.value = query
        reportData.value = res.data
        reportEndDate.value = $route.query.end
        reportColumnBy.value = columnBy.value
        reportPeriod.value = period.value
    } catch (err) {
        showError(getErrorMessage(err))
    } finally {
        loading.value = false
    }
}

const exportReport = () => {
    if (!loadedQuery.value) return
    const token = localStorage.getItem('token')
    const link = document.createElement('a')
    link.setAttribute('href', `${process.env.VUE_APP_BASE_URL}/export/accounting/balance-sheet-analytics${loadedQuery.value}&_token=${token}`)
    link.click()
}

const onApplyFilters = (filters) => {
    businessId.value = filters.business_id
    projectId.value = filters.project_id
    getReport()
}

const filterRefs = {
    business_id: businessId,
    project_id: projectId
}

const onRemoveChip = (key) => {
    if (!filterRefs[key]) return
    filterRefs[key].value = null
    getReport()
}

const onClearFilters = () => {
    businessId.value = null
    projectId.value = null
    getReport()
}

const onClickFilterButton = () => {
    $store.state.isModalOpen = true
}

const onFilter = (filter) => {
    decimals.value = filter.decimals
    showNonZero.value = filter.showNonZero
    showAccountCode.value = filter.showAccountCode
    $router.push({
        path: $route.path,
        query: { ...$route.query, decimals: filter.decimals, show_non_zero: filter.showNonZero, show_account_code: filter.showAccountCode }
    })
    getReport()
}

const toPeriodOptions = (intervals) => {
    return Object.keys(intervals)
        .filter(key => !['half_yearly', 'one_off'].includes(key))
        .map(key => ({ key, name: intervals[key] }))
}

const loadFilterOptions = () => {
    const companyQuery = `?company_id=${companyId.value}`
    Promise.all([
        fetchBusinessList(companyQuery).then(res => {
            businesses.value = res && res.data ? res.data : []
        }),
        fetchProjects(companyQuery).then(res => {
            projects.value = res && res.data ? res.data : []
        }),
        fetchHome().then(res => {
            const intervals = res && res.data && res.data.project_intervals ? res.data.project_intervals : {}
            periods.value = toPeriodOptions(intervals)
        })
    ]).catch(err => showError(getErrorMessage(err)))
}

onMounted(() => {
    getReport()
    loadFilterOptions()
})
</script>

<style scoped>
.bsa-page {
    min-height: calc(100vh - 185px);
}
.bsa-page-body {
    padding: 16px 20px 20px;
}
@media (max-width: 576px) {
    .bsa-page-body {
        padding: 14px 12px 16px;
    }
}
</style>
